// rc108 — Integración ByteOLT (Ajustes › Integraciones + alta de fibra).
// El navegador NUNCA habla con ByteOLT: todo va al API de ByteISP, que hace
// de proxy server-side con la llave bolt_ cifrada en config_sistema.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiCall } from "@/lib/api";

const KEY = "integraciones-byteolt";
const BASE = "/api/v1/integraciones/byteolt";

export type ByteoltSettings = {
  enabled: boolean;
  base_url: string | null;
  api_key_set: boolean;
  api_key_masked: string | null;
  required_scopes: string[];
};

export type ByteoltProbeResult = {
  success: boolean;
  error?: string;
  status?: number;
  key_name?: string;
  scopes?: string[];
  warning?: string;
  onus_huerfanas?: number | null;
  olts?: number | null;
};

export type OnuHuerfana = {
  serial: string;
  vendor: string | null;
  onu_index: string | null;
  rx_dbm: number | null;
  distancia_km: number | null;
  descripcion: string | null;
  olt: string | null;
  olt_id: string | null;
  olt_vendor: string | null;
  pon: number | null;
  // rc110 — presentes solo en el grupo "ya aprovisionadas sin cliente"
  estado?: string | null;
  vlan?: number | null;
  // rc113 — MAC de la ONU si ByteOLT ya la conoce (autollenar onu_mac → ByteACS)
  mac?: string | null;
};

// rc112 — OLTs de la planta (selector de destino del pre-registro)
export type OltInfo = {
  id: string;
  nombre: string;
  vendor: string | null;
  modelo?: string | null;
  read_only?: boolean;
};

export function useOltsByteolt(fetchEnabled: boolean) {
  return useQuery({
    queryKey: [KEY, "olts"],
    queryFn: () => apiCall<OltInfo[]>("get", `${BASE}/olts`),
    enabled: fetchEnabled,
    staleTime: 60_000,
    retry: 0,
  });
}

export type PlantillaServicio = {
  id: string;
  name: string;
  vlan_datos: number;
  vlan_aprov: number | null;
};

export type PerfilOnu = { id?: string | number; name?: string; nombre?: string };

export function useByteoltSettings() {
  return useQuery({
    queryKey: [KEY, "settings"],
    queryFn: () => apiCall<ByteoltSettings>("get", BASE),
  });
}

export function useGuardarByteolt() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { enabled: boolean; base_url?: string | null; api_key?: string | null }) =>
      apiCall<ByteoltSettings>("put", BASE, { json: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useProbarByteolt() {
  return useMutation({
    mutationFn: () => apiCall<ByteoltProbeResult>("post", `${BASE}/probar`, { json: {} }),
  });
}

/** ONUs por autorizar + (rc110) ya aprovisionadas SIN cliente, en vivo desde
 *  ByteOLT, más el flag `enabled` (el alta lo usa para saber si la fibra está
 *  disponible sin permisos de config). */
export function useOnusHuerfanas(fetchEnabled: boolean) {
  return useQuery({
    queryKey: [KEY, "onus-huerfanas"],
    queryFn: () =>
      apiCall<{
        enabled: boolean;
        onus: OnuHuerfana[];
        sin_cliente: OnuHuerfana[];
        byteacs_enabled?: boolean; // rc111
      }>("get", `${BASE}/onus-huerfanas`),
    enabled: fetchEnabled,
    staleTime: 10_000,
    retry: 0,
  });
}

// ═══ rc111 — consultas ACS del flujo de alta (perm contratos.write) ═════
export type EquipoAcsLink = {
  kind: string;
  client_nombre: string;
  client_codigo: string;
  contract_codigo: string;
  contract_estatus: string;
};

export type EquipoAcs = {
  found?: boolean | null;   // null = ByteACS viejo (< 0.49), no se puede saber
  enabled?: boolean;
  serial?: string;
  manufacturer?: string;
  product_class?: string;
  software_version?: string;
  last_inform_at?: string | null;
  lifecycle?: string;       // active | retired (almacén)
  retired_at?: string | null;
  retired_reason?: string;
  storage_note?: string;
  link?: EquipoAcsLink | null;
  pending?: { status: string; applied_at: string | null } | null;
};

export function useEquipoAcs(serial: string, fetchEnabled: boolean) {
  return useQuery({
    queryKey: [KEY, "acs-equipo", serial],
    queryFn: () =>
      apiCall<EquipoAcs>("get", `${BASE_ACS_OPS}/equipos/${encodeURIComponent(serial)}`),
    enabled: fetchEnabled && serial.length >= 8,
    staleTime: 15_000,
    retry: 0,
  });
}

export function useEquiposAlmacen(fetchEnabled: boolean) {
  return useQuery({
    queryKey: [KEY, "acs-almacen"],
    queryFn: () =>
      apiCall<{ enabled: boolean; equipos: EquipoAcs[] }>(
        "get", `${BASE_ACS_OPS}/equipos-almacen`,
      ),
    enabled: fetchEnabled,
    staleTime: 30_000,
    retry: 0,
  });
}

// ═══ ByteACS (rc110) — mismo patrón, prefijo propio ════════════════════
const BASE_ACS = "/api/v1/integraciones/byteacs";
const BASE_ACS_OPS = BASE_ACS; // alias legible para los endpoints operativos

export function useByteacsSettings() {
  return useQuery({
    queryKey: [KEY, "byteacs-settings"],
    queryFn: () => apiCall<ByteoltSettings>("get", BASE_ACS),
  });
}

export function useGuardarByteacs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { enabled: boolean; base_url?: string | null; api_key?: string | null }) =>
      apiCall<ByteoltSettings>("put", BASE_ACS, { json: data }),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useProbarByteacs() {
  return useMutation({
    mutationFn: () => apiCall<ByteoltProbeResult>("post", `${BASE_ACS}/probar`, { json: {} }),
  });
}

export function usePlantillasOlt(oltId: string | null) {
  return useQuery({
    queryKey: [KEY, "plantillas", oltId],
    queryFn: () => apiCall<PlantillaServicio[]>("get", `${BASE}/olts/${oltId}/plantillas`),
    enabled: !!oltId,
    staleTime: 60_000,
    retry: 0,
  });
}

export function usePerfilesOlt(oltId: string | null) {
  return useQuery({
    queryKey: [KEY, "perfiles", oltId],
    queryFn: () => apiCall<PerfilOnu[]>("get", `${BASE}/olts/${oltId}/perfiles`),
    enabled: !!oltId,
    staleTime: 60_000,
    retry: 0,
  });
}
